import { useEffect, useState } from 'react';
import './Camera.css';
import dayjs from 'dayjs';
import { Image } from 'antd';

export default function Camera() {
  const BUCKET_BASE = 'https://storage.googleapis.com/muller-camera/';
  const LIVE_REFRESH_MS = 60000;
  const PLAYBACK_FRAME_MS = 500;

  const [liveSrc, setLiveSrc] = useState(BUCKET_BASE + 'latest.jpg?t=' + Date.now());
  const [liveUpdated, setLiveUpdated] = useState();
  const [frameSrc, setFrameSrc] = useState();
  const [frameTime, setFrameTime] = useState();
  const [allImages, setAllImages] = useState([]);
  const [windowImages, setWindowImages] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [hours, setHours] = useState(8);
  const [frameIdx, setFrameIdx] = useState(false);
  const [timer, setTimer] = useState();

  function refreshLiveImage() {
    setLiveSrc(BUCKET_BASE + 'latest.jpg?t=' + Date.now());
  }

  function loadIndex() {
    return fetch(BUCKET_BASE + 'index.json?t=' + Date.now())
      .then(function (res) {
        if (!res.ok) throw new Error('index.json fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        const images = (data.images || []).slice().sort(function (a, b) {
          return new Date(a.timestamp) - new Date(b.timestamp);
        });
        setAllImages(images);
        setLiveUpdated(data.updated);
        applyWindow(images);
      })
      .catch(function (err) {
        console.error('Hill Cam timelapse: could not load index.json', err);
      });
  }

  function applyWindow(images = allImages, time = hours) {
    const filtered = images.filter(function (img) {
      return dayjs(img.timestamp).isAfter(dayjs().subtract(time, 'hours'));
    });
    setWindowImages(filtered);
    setFrameIdx(0);
  }

  function showFrame(idx, images = windowImages) {
    if (!images?.length) return;
    idx = Math.max(0, Math.min(idx, images.length - 1));
    const img = images[idx];
    setFrameSrc(img.url);
    setFrameTime(img.timestamp);
  }

  function nextFrame() {
    setFrameIdx((idx) => 1 + Math.max(0, Math.min(idx, windowImages.length - 1)));
  }

  function play() {
    if (windowImages?.length < 2) return;
    if (!timer) setTimer(setInterval(nextFrame, PLAYBACK_FRAME_MS));
    setPlaying(true);
  }

  function pause() {
    setPlaying(false);
    if (timer) {
      clearInterval(timer);
      setTimer();
    }
  }

  function onRangeSelect(time) {
    setHours(time);
    pause();
    applyWindow(undefined, time);
  }

  useEffect(() => {
    // Initial load
    refreshLiveImage();
    loadIndex();

    // Keep the live photo and the available window fresh
    setInterval(refreshLiveImage, LIVE_REFRESH_MS);
    setInterval(loadIndex, LIVE_REFRESH_MS);
  }, []);

  useEffect(() => {
    showFrame(frameIdx);
  }, [frameIdx]);

  return (
    <div id="hillcam-widget" className="hillcam">
      <div className="hillcam__live">
        <div className="hillcam__frame">
          <Image id="hillcam-live-img" alt="Current view of Muller Hill" src={liveSrc} />
          <div className="hillcam__badge">
            <span className="hillcam__dot"></span>
            <span id="hillcam-live-label">LIVE</span>
          </div>
        </div>
        <div className="hillcam__meta">
          <span id="hillcam-updated">
            {liveUpdated
              ? `Last Updated ${new Date(liveUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : 'Loading current conditions…'}
          </span>
        </div>
      </div>

      <div className="hillcam__timelapse">
        <div className="hillcam__controls">
          <div className="hillcam__ranges" role="group" aria-label="Timelapse duration">
            {[1, 4, 8, 12, 24].map((n) => (
              <button
                key={`${n}`}
                type="button"
                className={hours === n ? 'is-active' : ''}
                onClick={() => onRangeSelect(n)}
              >
                {n}h
              </button>
            ))}
          </div>
          <button
            type="button"
            id="hillcam-playpause"
            className="hillcam__play"
            aria-label="Play timelapse"
            onClick={() => {
              if (playing) pause();
              else play();
            }}
          >
            {playing ? (
              <svg id="hillcam-icon-pause" viewBox="0 0 24 24" width="16" height="16">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z" fill="currentColor" />
              </svg>
            ) : (
              <svg id="hillcam-icon-play" viewBox="0 0 24 24" width="16" height="16">
                <path d="M6 4l14 8-14 8z" fill="currentColor" />
              </svg>
            )}
          </button>
        </div>

        <div className="hillcam__stage">
          {frameSrc && (
            <Image
              id="hillcam-frame-img"
              alt="Timelapse frame"
              style={windowImages?.length === 0 ? { display: 'none' } : { display: 'block' }}
              src={frameSrc}
            />
          )}
          <div
            id="hillcam-empty"
            className="hillcam__empty"
            style={windowImages?.length === 0 ? { display: 'flex' } : { display: 'none' }}
          >
            Not enough images yet for this window — check back shortly.
          </div>
        </div>

        <div className="hillcam__scrub">
          <input
            type="range"
            id="hillcam-slider"
            min="0"
            max={Math.max(windowImages?.length - 1, 0)}
            value={frameIdx}
            step="1"
            aria-label="Timelapse position"
            onChange={(e) => {
              pause();
              console.log('select frame', e.target.value);
              setFrameIdx(parseInt(e.target.value, 10));
            }}
          />
          <span id="hillcam-frame-time">
            {frameTime && new Date(frameTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    </div>
  );
}
